import { useLayoutEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { type ScrollAlignT, type ScrollBehaviorT, SCROLL_ALIGN, SCROLL_BEHAVIOR } from '../../../common/virtual-grid';
import { timelineRowDescriptorsAtom } from '../atoms/frame-timeline-v4';
import { firstSelectedAnnotationIdAtom } from '../atoms/tool-atoms';

/**
 * Brings a newly selected annotation into view: its row pinned to the top (so its attribute
 * rows have room to reveal below) and the start of its first span centered horizontally.
 *
 * Layout effect (not passive) so the scroll lands in the same paint as the selection
 * highlight, matching the drag-autoscroll hooks and avoiding a one-frame flash.
 */
export function useSelectionAutoScroll(scrollToRow: (index: number, align?: ScrollAlignT, behavior?: ScrollBehaviorT) => void, scrollToColumn: (index: number, align?: ScrollAlignT, behavior?: ScrollBehaviorT) => void) {
  const selectedId = useAtomValue(firstSelectedAnnotationIdAtom);
  const rows = useAtomValue(timelineRowDescriptorsAtom);
  const scrolledIdRef = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    if (!selectedId) {
      scrolledIdRef.current = undefined;
      return;
    }
    if (selectedId === scrolledIdRef.current) return;
    const rowIndex = rows.findIndex(row => row.id === selectedId);
    if (rowIndex < 0) return;
    scrolledIdRef.current = selectedId;
    const firstSpanStart = rows[rowIndex]?.spans[0]?.start;
    if (firstSpanStart !== undefined) {
      /* Instant, not smooth: both axes share one scroll element, so a smooth horizontal scroll
       would be cancelled by the smooth vertical scroll below and never reach the column. */
      scrollToColumn(firstSpanStart + 1, SCROLL_ALIGN.center, SCROLL_BEHAVIOR.auto);
    }
    scrollToRow(rowIndex, SCROLL_ALIGN.start, SCROLL_BEHAVIOR.smooth);
  }, [selectedId, rows, scrollToRow, scrollToColumn]);
}